import React, { useState } from 'react';
import { User, Mail, Calendar, Shield, ChevronDown, ChevronRight, Edit, Trash2, Dumbbell, X } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const UserCard = ({ user, onEdit, onDelete, onCreateWorkout }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const api = getAuthenticatedApi();

    const getLastLoginText = () => {
        if (!user.last_login) return t('admin.userCard.lastLogin.never');

        const lastLogin = new Date(user.last_login);
        const now = new Date();
        
        const diffMs = now - lastLogin;
        const diffMin = Math.round(diffMs / 60000);
        const diffHours = Math.round(diffMs / 3600000);
        const diffDays = Math.round(diffMs / 86400000);
        const diffMonths = Math.round(diffDays / 30);
        
        if (diffMin < 60) return t('admin.userCard.lastLogin.minutesAgo', { minutes: diffMin });
        if (diffHours < 24) return t('admin.userCard.lastLogin.hoursAgo', { hours: diffHours });
        if (diffDays < 30) return t('admin.userCard.lastLogin.daysAgo', { days: diffDays });
        return t('admin.userCard.lastLogin.monthsAgo', { months: diffMonths });
    };

    const formatDateHelper = (dateString) => {
        if (!dateString) return t('admin.userCard.dateNotAvailable');
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('it-IT');
        } catch (e) {
            return t('admin.userCard.dateNotAvailable');
        }
    };

    const handleRemoveWorkout = async (e, workout) => {
        e.preventDefault();
        e.stopPropagation();
        
        const schedaId = workout.scheda_id;
        
        if (!schedaId) {
            console.error('Invalid workout data - missing scheda_id:', workout);
            return;
        }
        
        const confirmDelete = window.confirm('Sei sicuro di voler eliminare questa scheda?');
        if (confirmDelete) {
            try {
                await api.delete(`/schede.php?id=${schedaId}`);
                window.location.reload();
            } catch (err) {
                console.error('Error deleting workout:', err);
                alert('Errore durante l\'eliminazione della scheda');
            }
        }
    };
    
    const getRoleDisplayName = (roleName) => {
        switch (roleName) {
            case 'admin':
                return 'Amministratore';
            case 'standalone':
                return 'Utente Standalone';
            case 'gym':
                return 'Palestra';
            case 'trainer':
                return 'Trainer';
            case 'user':
                return 'Membro';
            case 'manager':
                return 'Manager';
            default:
                return roleName;
        }
    };
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-start justify-between">
                <div 
                    className="flex-1 flex items-start gap-2 cursor-pointer" 
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div 
                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 
                                  ${user.active ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' : 
                                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}
                    >
                        <User className="h-5 w-5" />
                    </div>
                    
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {user.name || user.username}
                            </h3>
                            
                            {!user.active && (
                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                    {t('admin.userCard.inactive')}
                                </span>
                            )}

                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                {getRoleDisplayName(user.role_name)}
                            </span>
                        </div>
                        
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            <span>{user.email || 'Nessuna email'}</span>
                        </div>

                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>Ultimo accesso: {getLastLoginText()}</span>
                        </div>
                    </div>
                </div>

                <div>
                    <button 
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">
                            Schede assegnate
                        </h4>
                        
                        {user.workouts && user.workouts.length > 0 ? (
                            <div className="space-y-2">
                                {user.workouts.map(workout => (
                                <div 
                                    key={workout.id} 
                                    className="flex items-center justify-between p-2 rounded-md bg-gray-50 dark:bg-gray-700"
                                >
                                    <div className="flex items-center gap-2">
                                    <Dumbbell className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    <span className="text-gray-800 dark:text-gray-200">
                                        {workout.scheda_nome}
                                    </span>
                                    {(workout.active === "1" || workout.active === 1 || workout.active === true) ? (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                        Attiva
                                        </span>
                                    ) : (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                                        Inattiva
                                        </span>
                                    )}
                                    </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                                                Creata il {formatDateHelper(workout.data_creazione)}
                                            </div>
                                            <button
                                                onClick={() => onCreateWorkout(user, 'edit', workout)}
                                                className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                title="Modifica scheda"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => handleRemoveWorkout(e, workout)}
                                                className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                title="Rimuovi scheda"
                                                type="button"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Nessuna scheda assegnata
                            </p>
                        )}
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={() => onEdit(user)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <Edit className="h-4 w-4" />
                            Modifica
                        </button>
                        
                        <button
                            onClick={() => onDelete(user)}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                            <Trash2 className="h-4 w-4" />
                            Elimina
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserCard;